import { injectable } from '@servicetitan/react-ioc';
import {
    InputFieldState,
    FormValidators,
    isFormStateChanged,
    setFormStateValues,
    formStateToJS,
    commitFormState,
    CheckboxFieldState,
} from '@servicetitan/form';
import { FormState, ValidationResponse } from 'formstate';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';

import {
    AccountSettingsApi,
    EditUserDto,
    EditUserPasswordDto,
    UserDetails,
    UserPhone,
    UserPhoneCountryCode,
} from '../api/account-settings.api';
import { passwordIsValidFormat } from '../utils';
import { NotificationsService } from '@servicetitan/notifications';
import {
    isBadRequestError,
    isConflictRequestError,
    tryGetClientError,
} from '../../common/utils/api';
import { phoneNumberPatternValidator, phoneNumberRequiredValidator } from '../utils/validators';
import { existingMobilePhoneErrorMessage } from '../constants';
import { FeatureFlagStore } from '@servicetitan/launchdarkly-service';
import { FeatureFlags } from '../../common/models/feature-flags';

// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export type IAccountSettingsFormState = {
    name: InputFieldState<string>;
    email: InputFieldState<string>;
    officePhone: InputFieldState<UserPhone>;
    mobilePhone: InputFieldState<UserPhone>;
    userName: InputFieldState<string>;
    mobilePhoneLoading?: InputFieldState<boolean>;
    isMfaEnabled?: CheckboxFieldState;
};

@injectable()
export class AccountSettingsStore {
    accountSettingsForm: FormState<IAccountSettingsFormState>;

    @observable
    confirmMFAEnableDialogOpen = false;

    changePasswordForm: FormState<{
        oldPassword: InputFieldState<string>;
        password: InputFieldState<string>;
        confirmPassword: InputFieldState<string>;
    }>;

    @observable
    userData?: UserDetails;

    @observable
    loading = false;

    @observable
    modalOpen = false;

    @observable
    changePasswordLoading = false;

    constructor(
        private readonly accountSettingsApi: AccountSettingsApi,
        private readonly notificationsService: NotificationsService,
        private readonly featureFlagStore: FeatureFlagStore<FeatureFlags>
    ) {
        makeObservable(this);
        this.confirmPasswordValidator = this.confirmPasswordValidator.bind(this);
        this.changePasswordForm = new FormState({
            oldPassword: new InputFieldState('').validators(
                FormValidators.requiredWithCustomMessage('Old password is required.')
            ),
            password: new InputFieldState('').validators(
                FormValidators.requiredWithCustomMessage('Password is required.'),
                this.passwordValidator
            ),
            confirmPassword: new InputFieldState('').validators(
                FormValidators.requiredWithCustomMessage('Confirm Password is required.'),
                this.confirmPasswordValidator
            ),
        });

        let formstate: FormState<IAccountSettingsFormState>;

        if (this.featureFlagStore.flags.multiFactorAuthenticationIsEnabled) {
            formstate = new FormState<IAccountSettingsFormState>({
                name: new InputFieldState('').validators(
                    FormValidators.requiredWithCustomMessage('Name is required.')
                ),
                email: new InputFieldState('').validators(this.emailValidator),
                officePhone: new InputFieldState<UserPhone>({
                    countryCode: UserPhoneCountryCode.US,
                    phone: '',
                }).validators(phoneNumberPatternValidator),
                mobilePhone: new InputFieldState<UserPhone>({
                    countryCode: UserPhoneCountryCode.US,
                    phone: '',
                }).validators(
                    phoneNumberRequiredValidator,
                    phoneNumberPatternValidator,
                    async (value: UserPhone): Promise<ValidationResponse> => {
                        return this.mobilePhoneExistingValidator(formstate, value);
                    }
                ),
                userName: new InputFieldState('').validators(
                    FormValidators.requiredWithCustomMessage('Username is required.')
                ),
                mobilePhoneLoading: new InputFieldState<boolean>(false),
                isMfaEnabled: new CheckboxFieldState(false),
            });
        } else {
            formstate = new FormState<IAccountSettingsFormState>({
                name: new InputFieldState('').validators(
                    FormValidators.requiredWithCustomMessage('Name is required.')
                ),
                email: new InputFieldState('').validators(this.emailValidator),
                officePhone: new InputFieldState<UserPhone>({
                    countryCode: UserPhoneCountryCode.US,
                    phone: '',
                }).validators(phoneNumberPatternValidator),
                mobilePhone: new InputFieldState<UserPhone>({
                    countryCode: UserPhoneCountryCode.US,
                    phone: '',
                }).validators(
                    phoneNumberPatternValidator,
                    async (value: UserPhone): Promise<ValidationResponse> => {
                        return this.mobilePhoneExistingValidator(formstate, value);
                    }
                ),
                userName: new InputFieldState('').validators(
                    FormValidators.requiredWithCustomMessage('Username is required.')
                ),
                mobilePhoneLoading: new InputFieldState<boolean>(false),
            });
        }

        this.accountSettingsForm = formstate;
    }

    @computed
    get isFormStateChanged() {
        return isFormStateChanged(this.accountSettingsForm).get();
    }

    @action
    openConfirmMFAEnableDialog = () => {
        this.confirmMFAEnableDialogOpen = true;
    };

    @action
    closeConfirmMFAEnableDialog = () => {
        this.confirmMFAEnableDialogOpen = false;
    };

    @action
    getUserDetails = async () => {
        try {
            this.loading = true;
            const { data } = await this.accountSettingsApi.getUserDetails();
            if (this.featureFlagStore.flags.multiFactorAuthenticationIsEnabled) {
                setFormStateValues(this.accountSettingsForm, {
                    ...data,
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    isMfaEnabled: data.isMfaEnabled,
                    mobilePhone: data.mobilePhone?.phone
                        ? data.mobilePhone
                        : {
                              countryCode: UserPhoneCountryCode.US,
                              phone: '',
                          },
                    officePhone: data.officePhone?.phone
                        ? data.officePhone
                        : {
                              countryCode: UserPhoneCountryCode.US,
                              phone: '',
                          },
                });
            } else {
                setFormStateValues(this.accountSettingsForm, {
                    ...data,
                    isMfaEnabled: undefined,
                    officePhone: data.officePhone?.phone
                        ? data.officePhone
                        : {
                              countryCode: UserPhoneCountryCode.US,
                              phone: '',
                          },
                    mobilePhone: data.mobilePhone?.phone
                        ? data.mobilePhone
                        : {
                              countryCode: UserPhoneCountryCode.US,
                              phone: '',
                          },
                });
            }

            runInAction(() => {
                this.userData = data;
            });
        } catch (error) {
            // console.log(error);
            const clientError = tryGetClientError(error);
            if (clientError) {
                this.notificationsService.error({
                    title: clientError.getTitle(),
                    message: clientError.getMessage(),
                    duration: 0,
                });
            }
        } finally {
            this.loading = false;
        }
    };

    @action
    editUserDetails = async () => {
        const { hasError } = await this.accountSettingsForm.validate();
        if (hasError) {
            return;
        }
        if (
            this.accountSettingsForm.$.isMfaEnabled?.value &&
            this.userData?.isMfaEnabled === false
        ) {
            this.openConfirmMFAEnableDialog();
        } else {
            this.saveChanges();
        }
    };

    @action
    saveChanges = async () => {
        try {
            this.loading = true;
            const formdata = formStateToJS(this.accountSettingsForm);

            const dto = new EditUserDto(formdata);
            if (!dto.officePhone?.phone) {
                dto.officePhone = null;
            }
            if (!dto.mobilePhone?.phone) {
                dto.mobilePhone = null;
            }
            
            const response = await this.accountSettingsApi.editUserDetails(dto);

            if (response.headers.location) {
                return (window.location.href = response.headers.location);
            }

            runInAction(() => {
                this.closeConfirmMFAEnableDialog();
                commitFormState(this.accountSettingsForm);
            });
        } catch (error) {
            // console.log(error);
            const clientError = tryGetClientError(error);
            if (clientError) {
                this.notificationsService.error({
                    title: clientError.getTitle(),
                    message: clientError.getMessage(),
                    duration: 0,
                });
            }
        } finally {
            this.loading = false;
        }
    };

    @action
    discardChanges = () => {
        this.closeConfirmMFAEnableDialog();
    };

    @action changePassword = async () => {
        try {
            await this.changePasswordForm.validate();
            if (this.changePasswordForm.hasError) {
                return;
            }
            this.changePasswordLoading = true;
            const dto = new EditUserPasswordDto(formStateToJS(this.changePasswordForm));
            await this.accountSettingsApi.editUserPassword(dto);
            runInAction(() => {
                this.changePasswordForm.reset();
                this.toggleModal(false);
            });
        } catch (error) {
            // console.log(error);
            const clientError = tryGetClientError(error);
            if (clientError) {
                this.notificationsService.error({
                    title: clientError.getTitle(),
                    message: clientError.getMessage(),
                    duration: 0,
                });
            }
        } finally {
            this.changePasswordLoading = false;
        }
    };

    @action
    toggleModal = (open: boolean) => {
        this.modalOpen = open;
    };

    @action
    openModal = () => {
        this.toggleModal(true);
    };

    @action
    closeModal = () => {
        this.toggleModal(false);
    };

    private mobilePhoneExistingValidator = async (
        formstate: FormState<IAccountSettingsFormState>,
        value: UserPhone
    ): Promise<ValidationResponse> => {
        if (!value.phone) {
            return;
        }
        try {
            formstate.$.mobilePhoneLoading?.onChange(true);
            await this.accountSettingsApi.validateUserInfo({
                email: null,
                mobilePhone: value,
            });
        } catch (error) {
            if (
                isConflictRequestError(error) &&
                error.response?.data.errors.errorMessage.includes('MobilePhone')
            ) {
                return existingMobilePhoneErrorMessage;
            }

            if (isBadRequestError(error) && error.response?.data.errors.MobilePhone?.length) {
                return error.response?.data.errors.MobilePhone[0];
            }

            const clientError = tryGetClientError(error);
            if (clientError) {
                this.notificationsService.error({
                    title: clientError.getTitle(),
                    message: clientError.getMessage()
                }, );
                return clientError.getTitle();
            }
        } finally {
            formstate.$.mobilePhoneLoading?.onChange(false);
        }
    };

    private passwordValidator(value: string) {
        return (
            !passwordIsValidFormat(value) &&
            'Password must be 10 or more characters and contain at least one uppercase and one lowercase letter (Aa).'
        );
    }

    private emailValidator(value: string) {
        return !FormValidators.emailFormatIsValid(value) && 'Please enter a valid email.';
    }

    private confirmPasswordValidator(value: string): ValidationResponse {
        return this.changePasswordForm.$.password.value !== value && `Passwords don't match`;
    }
}
